const CONTACT = "castle.keve@gmail.com";
const UPDATED = "June 2026";

const SECTIONS = [
  {
    t: "1. Who we are",
    p: [
      "Mango Notes (\"the app\") is a notes application owned and operated by Denia Cardona (\"we\", \"us\"). It is available on the web and on mobile devices. This policy explains what information we collect, how we use it, and the choices you have.",
    ],
  },
  {
    t: "2. Information we collect",
    l: [
      "Account information: your email address, used to sign you in with a one-time code or Google sign-in (we request only your email, not your profile or photos). Guest mode stores notes only on your device until you sign in.",
      "Content you create: notes, checklists, labels, colors, reminders, and attachments you add (images, documents, audio, video, drawings and camera captures).",
      "Encrypted notes and file transfers: encrypted notes are locked on your device with a password before they are sent, so we only store unreadable data and never the password. Files you send by link are stored for 7 days and then expire; you can delete a transfer earlier at any time.",
      "Payment information: if you send an optional tip, the payment is processed by Stripe, PayPal or Google Pay. We never see or store your full card or bank details; we only keep the amount, status and a transaction reference.",
      "Basic usage and device data: anonymous analytics (such as pages viewed and app errors) to keep the app working and improve it.",
    ],
  },
  {
    t: "3. Device permissions",
    l: [
      "Camera: only when you choose to take a photo or video for a note.",
      "Microphone: only when you record an audio note.",
      "Photos / files: only when you choose to attach a file.",
      "Notifications: only to deliver reminders you set.",
    ],
    p: ["You can deny or revoke any permission in your device settings; the rest of the app keeps working."],
  },
  {
    t: "4. How we use your information",
    l: [
      "To provide the app: store, sync and back up your notes across your devices.",
      "To sign you in and keep your account secure.",
      "To send service emails, such as sign-in codes, reminders, and share or mention notifications.",
      "To process optional tips and keep records required by law.",
    ],
    p: ["We do not sell your personal information, and we do not use your notes for advertising."],
  },
  {
    t: "5. Offline storage and sync",
    p: [
      "Notes are saved on your device so the app works offline, and are synced to our secure servers when you are signed in and online. Locked notes are protected by a passphrase that is stored only as a hash.",
    ],
  },
  {
    t: "6. Sharing with third parties",
    p: ["We share data only with service providers that help us run the app:"],
    l: [
      "Cloud hosting, database and file storage providers (to store your notes and attachments).",
      "Email delivery provider (to send sign-in codes and notifications).",
      "Stripe, PayPal and Google Pay (to process optional tips).",
      "Google (only if you choose Google sign-in).",
      "An analytics provider (anonymous usage statistics).",
    ],
    p2: [
      "If you share a note by link, anyone with that link can view it until you turn sharing off. We may also disclose information if required by law.",
    ],
  },
  {
    t: "7. Data security",
    p: [
      "All data is sent over encrypted connections (HTTPS). Access to our servers is restricted. No method of storage is 100% secure, but we work to protect your information.",
    ],
  },
  {
    t: "8. Data retention and deletion",
    p: [
      "We keep your data while your account is active. Notes you move to Trash can be permanently deleted at any time.",
      "You can delete your account and all of its data directly in the app (side menu → Delete account). You can also request deletion by emailing us at the address below; we will process it within 30 days. Payment records may be kept where the law requires.",
    ],
  },
  {
    t: "9. Your rights",
    p: [
      "Depending on where you live, you may have the right to access, correct, export or delete your data. You can export your notes from within the app at any time. For any other request, contact us.",
    ],
  },
  {
    t: "10. Children",
    p: [
      "Mango Notes is not directed at children under 13, and we do not knowingly collect personal information from them. If you believe a child has provided us data, contact us and we will delete it.",
    ],
  },
  {
    t: "11. Changes to this policy",
    p: [
      "We may update this policy from time to time. Changes will be posted on this page with a new \"Last updated\" date.",
    ],
  },
];

const List = ({ items }) => (
  <ul className="list-disc pl-5 space-y-2">
    {items.map((x) => <li key={x}>{x}</li>)}
  </ul>
);

export default function PrivacyPolicy() {
  return (
    <div data-testid="privacy-policy-page" className="min-h-screen bg-background text-foreground">
      <main className="max-w-3xl mx-auto px-6 py-16 sm:py-24">
        <a href="/" data-testid="privacy-back-link" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
          ← Mango Notes
        </a>
        <h1 className="mt-6 text-4xl sm:text-5xl font-semibold tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
          Privacy Policy
        </h1>
        <p data-testid="privacy-updated" className="mt-3 text-sm text-muted-foreground">Last updated: {UPDATED} · Owner: Denia Cardona</p>
        <div className="mt-12 space-y-10 text-sm sm:text-base leading-relaxed text-foreground/85">
          {SECTIONS.map((s) => (
            <section key={s.t} className="space-y-3">
              <h2 className="text-base md:text-lg font-semibold text-foreground">{s.t}</h2>
              {s.p?.map((x) => <p key={x}>{x}</p>)}
              {s.l && <List items={s.l} />}
              {s.p2?.map((x) => <p key={x}>{x}</p>)}
            </section>
          ))}
          <section className="space-y-3">
            <h2 className="text-base md:text-lg font-semibold text-foreground">12. Contact us</h2>
            <p>
              Questions or deletion requests:{" "}
              <a data-testid="privacy-contact-email" href={`mailto:${CONTACT}`} className="underline underline-offset-4 hover:text-foreground">
                {CONTACT}
              </a>
              . See also our{" "}
              <a data-testid="privacy-terms-link" href="/terms" className="underline underline-offset-4 hover:text-foreground">Terms of Service</a>.
            </p>
          </section>
        </div>
      </main>
    </div>
  );
}
